import { clear, del, get, set } from 'idb-keyval';

import { QaCacheState } from './qaCacheSlice';

const IDB_KEY_PREFIX = 'qa-cache';
const DEBOUNCE_MS = 2000;

let debounceTimer: ReturnType<typeof setTimeout> | undefined;

const storageKey = (userId: string): string => `${IDB_KEY_PREFIX}:${userId}`;

/**
 * Persists the QA cache state to IndexedDB, debounced to avoid write storms.
 *
 * Why:
 *   Cache updates happen frequently during progressive loads and delta syncs.
 *   Debouncing at 2s collapses rapid writes into one IDB transaction.
 *
 * How:
 *   Call on every Redux cache update. Keys are scoped to the current user ID
 *   to prevent data leaking between accounts on shared workstations.
 */
export const persistQaCacheDebounced = (userId: string, state: QaCacheState): void => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    set(storageKey(userId), state).catch((err) =>
      console.error('Failed to persist QA cache to IDB', err)
    );
  }, DEBOUNCE_MS);
};

export const persistQaCacheImmediate = (userId: string, state: QaCacheState): Promise<void> =>
  set(storageKey(userId), state);

export const loadQaCache = async (userId: string): Promise<QaCacheState | undefined> => {
  try {
    return await get<QaCacheState>(storageKey(userId));
  } catch (err) {
    console.error('Failed to load QA cache from IDB', err);
    return undefined;
  }
};

export const clearQaCacheIdb = async (userId?: string): Promise<void> => {
  try {
    if (userId) {
      await del(storageKey(userId));
    } else {
      await clear();
    }
  } catch (err) {
    console.error('Failed to clear QA cache from IDB', err);
  }
};
